export type TypeJour = 
  | 'CENTRE_FORMATION'
  | 'ENTREPRISE'
  | 'EPREUVE_ECRITE'
  | 'ECF'
  | 'FERIE'
  | 'VACANCES';

export interface JourCalendrierLocal {
  date: Date;
  type: TypeJour;
}

/**
 * Crée un tableau en mémoire contenant tous les jours du 1er Septembre 
 * de l'année N au 31 Août de l'année N+1.
 */
export function initialiserAnnee(anneeDebut: number): JourCalendrierLocal[] {
  const jours: JourCalendrierLocal[] = [];
  const dateCourante = new Date(anneeDebut, 8, 1); // 1er Septembre
  const dateFin = new Date(anneeDebut + 1, 7, 31); // 31 Août année N+1

  while (dateCourante <= dateFin) {
    jours.push({
      date: new Date(dateCourante),
      type: 'ENTREPRISE',
    });
    dateCourante.setDate(dateCourante.getDate() + 1);
  }

  return jours;
}

/**
 * Calcule la date de Pâques pour une année donnée (Algorithme de Meeus/Jones/Butcher)
 */
function getPaques(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month, day);
}

/**
 * Fonction qui identifie les jours fériés légaux français pour une année civile
 * incluant le 20 décembre (Abolition de l'esclavage à La Réunion).
 */
export function getJoursFeries(annee: number): Date[] {
  const paques = getPaques(annee);
  const lundiPaques = new Date(paques);
  lundiPaques.setDate(paques.getDate() + 1);
  
  const ascension = new Date(paques);
  ascension.setDate(paques.getDate() + 39);
  
  const lundiPentecote = new Date(paques);
  lundiPentecote.setDate(paques.getDate() + 50);

  return [
    new Date(annee, 0, 1),   // Jour de l'an
    lundiPaques,             // Lundi de Pâques
    new Date(annee, 4, 1),   // Fête du travail
    new Date(annee, 4, 8),   // Victoire 1945
    ascension,               // Ascension
    lundiPentecote,          // Lundi de Pentecôte
    new Date(annee, 6, 14),  // Fête nationale
    new Date(annee, 7, 15),  // Assomption
    new Date(annee, 10, 1),  // Toussaint
    new Date(annee, 10, 11), // Armistice 1918
    new Date(annee, 11, 20), // Abolition de l'esclavage (La Réunion)
    new Date(annee, 11, 25), // Noël
  ];
}

/**
 * Applique les jours fériés sur un tableau de jours existant
 */
export function appliquerJoursFeries(jours: JourCalendrierLocal[]): JourCalendrierLocal[] {
  const annees = [...new Set(jours.map(j => j.date.getFullYear()))];
  const tousFeries = annees.flatMap(annee => getJoursFeries(annee));

  return jours.map(jour => {
    const estFerie = tousFeries.some(
      f => f.getDate() === jour.date.getDate() && 
           f.getMonth() === jour.date.getMonth() && 
           f.getFullYear() === jour.date.getFullYear()
    );
    
    if (estFerie) {
      return { ...jour, type: 'FERIE' };
    }
    return jour;
  });
}

/**
 * Applique un rythme d'alternance sans écraser les jours fériés ni les vacances
 */
export function appliquerRythmeAlternance(
  jours: JourCalendrierLocal[],
  joursCFA: number[] // 0 = Dimanche, 1 = Lundi, etc.
): JourCalendrierLocal[] {
  return jours.map(jour => {
    if (jour.type === 'FERIE' || jour.type === 'VACANCES') {
      return jour;
    }
    
    const dayOfWeek = jour.date.getDay();
    if (joursCFA.includes(dayOfWeek)) {
      return { ...jour, type: 'CENTRE_FORMATION' };
    }
    
    if (jour.type === 'CENTRE_FORMATION') {
       return { ...jour, type: 'ENTREPRISE' };
    }
    
    return jour;
  });
}

/**
 * Palette d'affectation du calendrier — une seule source pour la grille de
 * saisie, l'aperçu du dossier et la légende. Les teintes sont pastel pour que
 * douze mois affichés d'un coup restent lisibles : c'est le chiffre du jour qui
 * doit se lire, pas l'aplat. Le texte garde le contraste AA sur son fond.
 */
export interface StyleJour {
  /** Libellé affiché en légende et en infobulle. */
  libelle: string;
  /** Classes du pavé de jour (fond + texte + liseré). */
  pave: string;
  /** Pastille de légende et bouton de pinceau. */
  pastille: string;
  /** Couleur pleine, pour les exports qui ne connaissent pas Tailwind. */
  hex: string;
}

export const STYLES_JOUR: Record<TypeJour, StyleJour> = {
  CENTRE_FORMATION: {
    libelle: 'Centre de formation',
    pave: 'bg-[#E3EDFB] text-[#104890] ring-1 ring-inset ring-[#BDD4F2]',
    pastille: 'bg-[#E3EDFB] text-[#104890] ring-1 ring-[#BDD4F2]',
    hex: '#E3EDFB',
  },
  ENTREPRISE: {
    libelle: 'Entreprise',
    pave: 'bg-[#FCF1D6] text-[#7A5A00] ring-1 ring-inset ring-[#F0DCA0]',
    pastille: 'bg-[#FCF1D6] text-[#7A5A00] ring-1 ring-[#F0DCA0]',
    hex: '#FCF1D6',
  },
  EPREUVE_ECRITE: {
    libelle: 'Épreuve écrite',
    pave: 'bg-[#EDE5FA] text-[#553093] ring-1 ring-inset ring-[#D7C6F2]',
    pastille: 'bg-[#EDE5FA] text-[#553093] ring-1 ring-[#D7C6F2]',
    hex: '#EDE5FA',
  },
  ECF: {
    libelle: 'ECF',
    pave: 'bg-[#E4F2E2] text-[#2C6431] ring-1 ring-inset ring-[#C2E1BE]',
    pastille: 'bg-[#E4F2E2] text-[#2C6431] ring-1 ring-[#C2E1BE]',
    hex: '#E4F2E2',
  },
  VACANCES: {
    libelle: 'Vacances',
    pave: 'bg-[#F1EFE9] text-[#6E6A63] ring-1 ring-inset ring-[#E0DBD1]',
    pastille: 'bg-[#F1EFE9] text-[#6E6A63] ring-1 ring-[#E0DBD1]',
    hex: '#F1EFE9',
  },
  FERIE: {
    libelle: 'Férié',
    pave: 'bg-[#FAE4E4] text-[#9B2C2C] ring-1 ring-inset ring-[#F0C6C6]',
    pastille: 'bg-[#FAE4E4] text-[#9B2C2C] ring-1 ring-[#F0C6C6]',
    hex: '#FAE4E4',
  },
};

/** Ordre d'affichage de la légende : du plus fréquent au plus ponctuel. */
export const ORDRE_TYPES: TypeJour[] = [
  'CENTRE_FORMATION',
  'ENTREPRISE',
  'EPREUVE_ECRITE',
  'ECF',
  'VACANCES',
  'FERIE',
];

/** Nombre de jours par affectation — la légende annonce un volume, pas un code couleur. */
export function compterParType(jours: JourCalendrierLocal[]): Record<TypeJour, number> {
  const compte: Record<TypeJour, number> = {
    CENTRE_FORMATION: 0,
    ENTREPRISE: 0,
    EPREUVE_ECRITE: 0,
    ECF: 0,
    VACANCES: 0,
    FERIE: 0,
  };
  jours.forEach((jour) => {
    compte[jour.type] += 1;
  });
  return compte;
}

/**
 * Numéro de semaine ISO 8601 — le modèle Excel remis par le client porte la
 * colonne « Sem. », et c'est par ce numéro que les plannings se discutent.
 */
export function numeroSemaineIso(date: Date): number {
  const reference = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // Jeudi de la semaine courante : il détermine l'année ISO à lui seul.
  reference.setDate(reference.getDate() + 3 - ((reference.getDay() + 6) % 7));
  const premierJeudi = new Date(reference.getFullYear(), 0, 4);
  premierJeudi.setDate(premierJeudi.getDate() + 3 - ((premierJeudi.getDay() + 6) % 7));
  return 1 + Math.round((reference.getTime() - premierJeudi.getTime()) / (7 * 86400000));
}
